/**
 * Dates as `YYYY-MM-DD` strings. No `Date` object ever holds a calendar day.
 *
 * WHY: `new Date("2026-03-01")` is parsed as UTC midnight. Read back in IST
 * (UTC+5:30) that instant is 2026-02-28 18:30 — the previous DAY, and for the
 * first of a month, the previous MONTH. `Budget.periodStart` stored a timestamp
 * and did exactly this: 80 budgets sat one day before the month they belonged
 * to, an exact-equality lookup found none of them, and the page reported "No
 * budgets set" for five months.
 *
 * Everything here is string and integer arithmetic for that reason.
 */

export type PeriodKey = "week" | "monthly" | "quarterly" | "half" | "fy";

export interface DateRange {
  start: string;
  end: string;
}

const DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

function parts(date: string): { y: number; m: number; d: number } {
  const m = DATE_RE.exec(date);
  if (!m) throw new Error(`expected YYYY-MM-DD, got ${JSON.stringify(date)}`);
  return { y: Number(m[1]), m: Number(m[2]), d: Number(m[3]) };
}

function iso(y: number, m: number, d: number): string {
  return `${String(y).padStart(4, "0")}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
}

/** Days in a month, Gregorian leap rule, no Date involved. */
export function daysInMonth(y: number, m: number): number {
  if (m === 2) return (y % 4 === 0 && y % 100 !== 0) || y % 400 === 0 ? 29 : 28;
  return [4, 6, 9, 11].includes(m) ? 30 : 31;
}

export function monthStart(date: string): string {
  const { y, m } = parts(date);
  return iso(y, m, 1);
}

export function monthEnd(date: string): string {
  const { y, m } = parts(date);
  return iso(y, m, daysInMonth(y, m));
}

/** Indian financial year: 1 April to 31 March. */
export function getFYBounds(date: string): DateRange {
  const { y, m } = parts(date);
  const startYear = m >= 4 ? y : y - 1;
  return { start: iso(startYear, 4, 1), end: iso(startYear + 1, 3, 31) };
}

export function getPeriodBounds(period: PeriodKey, ref: string): DateRange {
  const { y, m, d } = parts(ref);
  switch (period) {
    case "fy":
      return getFYBounds(ref);
    case "monthly":
      return { start: monthStart(ref), end: monthEnd(ref) };
    case "quarterly": {
      const q = Math.floor((m - 1) / 3);
      const sm = q * 3 + 1;
      const em = sm + 2;
      return { start: iso(y, sm, 1), end: iso(y, em, daysInMonth(y, em)) };
    }
    case "half": {
      // Fiscal half, not calendar half: H1 is Apr-Sep, H2 is Oct-Mar and
      // crosses the calendar year boundary — matches the Indian FY, and
      // matches web's existing `halfYearBounds` (see task-3-report.md: the
      // brief's original draft used calendar halves, Jan-Jun/Jul-Dec, which
      // disagreed with the shipped app and was corrected here).
      if (m >= 4 && m <= 9) {
        return { start: iso(y, 4, 1), end: iso(y, 9, 30) };
      }
      const startYear = m >= 10 ? y : y - 1;
      return { start: iso(startYear, 10, 1), end: iso(startYear + 1, 3, 31) };
    }
    case "week": {
      // Monday-start, computed by day-number arithmetic rather than Date.getDay
      // so no timezone can shift which week a date falls in.
      const dayNumber = toDayNumber(y, m, d);
      // `% 7` alone yields a negative result for a negative `dayNumber`
      // (dates before 1970-01-01) in JS, since `%` is remainder, not modulo —
      // the extra `+ 7) % 7` normalizes it back into [0, 6].
      const dow = ((dayNumber + 3) % 7 + 7) % 7; // 1970-01-01 was a Thursday
      const startNum = dayNumber - dow;
      return { start: fromDayNumber(startNum), end: fromDayNumber(startNum + 6) };
    }
  }
}

/** Days since 1970-01-01, by civil-date arithmetic (Howard Hinnant's algorithm). */
function toDayNumber(y: number, m: number, d: number): number {
  const yy = m <= 2 ? y - 1 : y;
  const era = Math.floor(yy / 400);
  const yoe = yy - era * 400;
  const doy = Math.floor((153 * (m + (m > 2 ? -3 : 9)) + 2) / 5) + d - 1;
  const doe = yoe * 365 + Math.floor(yoe / 4) - Math.floor(yoe / 100) + doy;
  return era * 146097 + doe - 719468;
}

function fromDayNumber(z: number): string {
  const zz = z + 719468;
  const era = Math.floor(zz / 146097);
  const doe = zz - era * 146097;
  const yoe = Math.floor((doe - Math.floor(doe / 1460) + Math.floor(doe / 36524) - Math.floor(doe / 146096)) / 365);
  const yy = yoe + era * 400;
  const doy = doe - (365 * yoe + Math.floor(yoe / 4) - Math.floor(yoe / 100));
  const mp = Math.floor((5 * doy + 2) / 153);
  const d = doy - Math.floor((153 * mp + 2) / 5) + 1;
  const m = mp + (mp < 10 ? 3 : -9);
  return iso(m <= 2 ? yy + 1 : yy, m, d);
}
